import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  Menu, X, ShoppingBag, Sprout, ArrowUpRight, Phone, Mail, MapPin, Instagram, Facebook, Send,
  House, Store, PackageSearch, UserRound, Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { koperasiInfo } from "@/lib/dummyData";
import { useCart, cartPathFor } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { isAdminRole } from "@/lib/navigation";
import { useInstallPrompt } from "@/lib/pwa";
import MobileTabBar from "@/components/MobileTabBar";

const navItems = [
  { to: "/", label: "Beranda" },
  { to: "/profil", label: "Profil" },
  { to: "/toko", label: "Toko" },
  { to: "/lacak", label: "Lacak Pesanan" },
  { to: "/berita", label: "Berita" },
];

export default function PortalLayout() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { count } = useCart();
  const { user } = useAuth();
  const { canInstall, promptInstall } = useInstallPrompt();
  const accountTo = user ? (isAdminRole(user.role) ? "/admin" : "/anggota") : "/masuk";

  // Navigasi bawah (mobile): menu utama; Profil, Berita, dll. tetap di menu atas.
  const tabs = [
    { to: "/", label: "Beranda", icon: House, end: true },
    { to: "/toko", label: "Toko", icon: Store },
    { to: cartPathFor(user), label: "Keranjang", icon: ShoppingBag, badge: count },
    { to: "/lacak", label: "Lacak", icon: PackageSearch },
    { to: accountTo, label: user ? "Akun" : "Masuk", icon: UserRound },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-cream">
      {/* Header */}
      <header className="sticky top-0 z-40 glass-nav pt-safe">
        <div className="mx-auto max-w-7xl px-4 sm:px-5 lg:px-10">
          <div className="flex items-center justify-between h-14 lg:h-20">
            <Link to="/" className="flex items-center gap-2.5 group min-w-0" data-testid="brand-logo-link">
              <span className="w-9 h-9 lg:w-10 lg:h-10 rounded-xl lg:rounded-2xl bg-forest text-white grid place-items-center shadow-sm group-hover:rotate-[-6deg] transition shrink-0">
                <Sprout size={19} strokeWidth={2.2} />
              </span>
              <div className="leading-tight min-w-0">
                <div className="font-display font-bold text-base lg:text-lg text-ink tracking-tight truncate">
                  <span className="lg:hidden">Andalan Dapur</span>
                  <span className="hidden lg:inline">Koperasi Andalan Dapur Nusantara</span>
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-ink/55">
                  <span className="lg:hidden">Koperasi · </span>Tumbuh Bersama
                </div>
              </div>
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`portal-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      isActive ? "bg-forest text-white" : "text-ink/75 hover:bg-white/70"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <Link to={cartPathFor(user)} className="relative w-10 h-10 rounded-full hidden lg:grid place-items-center hover:bg-white/60 transition" data-testid="portal-cart-button">
                <ShoppingBag size={18} className="text-ink" />
                {count > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-terracotta text-white text-[10px] font-bold min-w-4 h-4 px-1 grid place-items-center rounded-full" data-testid="portal-cart-badge">
                    {count > 99 ? "99+" : count}
                  </span>
                )}
              </Link>
              <Link to="/masuk" className="hidden md:inline-flex" data-testid="portal-login-link">
                <Button variant="ghost" className="rounded-full text-ink/75 hover:bg-white/60">Masuk</Button>
              </Link>
              <Link to="/daftar" className="hidden md:inline-flex" data-testid="portal-register-cta">
                <Button className="rounded-full bg-forest hover:bg-forest-dark text-white shadow-sm">
                  Gabung Sekarang <ArrowUpRight size={16} />
                </Button>
              </Link>
              <button
                className="lg:hidden w-10 h-10 rounded-full grid place-items-center hover:bg-white/60"
                onClick={() => setOpen((v) => !v)}
                data-testid="portal-mobile-menu-toggle"
                aria-label="Menu lainnya"
                aria-expanded={open}
              >
                {open ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>

          {open && (
            <div className="lg:hidden pb-4 flex flex-col gap-1 animate-fade-up">
              {[...navItems, { to: "/katalog", label: "Katalog Produk" }, { to: "/kontak", label: "Kontak" }].map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  end={n.to === "/"}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-xl text-sm font-medium ${
                      isActive ? "bg-forest text-white" : "text-ink/75 hover:bg-white"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              {canInstall && (
                <button
                  onClick={() => { setOpen(false); promptInstall(); }}
                  className="px-4 py-3 rounded-xl text-sm font-medium text-forest hover:bg-white flex items-center gap-2 text-left"
                  data-testid="portal-install-app"
                >
                  <Download size={16} /> Pasang Aplikasi
                </button>
              )}
              {!user && (
                <div className="flex gap-2 mt-2">
                  <Link to="/masuk" onClick={() => setOpen(false)} className="flex-1"><Button variant="outline" className="w-full rounded-full">Masuk</Button></Link>
                  <Link to="/daftar" onClick={() => setOpen(false)} className="flex-1"><Button className="w-full rounded-full bg-forest text-white">Daftar</Button></Link>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      <main key={location.pathname} className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-ink text-cream/90 mt-16 lg:mt-24 pb-tabbar">
        <div className="mx-auto max-w-7xl px-5 lg:px-10 py-10 lg:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-2.5 mb-4">
                <span className="w-10 h-10 rounded-2xl bg-amber-warm text-ink grid place-items-center">
                  <Sprout size={20} />
                </span>
                <div className="font-display font-bold text-xl">Koperasi Andalan Dapur Nusantara</div>
              </div>
              <p className="text-cream/70 text-sm leading-relaxed max-w-md">
                {koperasiInfo.tagline}. Membangun ekonomi gotong-royong yang transparan, modern, dan berdaya saing — sejak {koperasiInfo.founded}.
              </p>
              <div className="mt-6 flex gap-3">
                <a href="#" className="w-10 h-10 rounded-full border border-cream/20 grid place-items-center hover:bg-cream/10 transition" aria-label="Instagram"><Instagram size={16} /></a>
                <a href="#" className="w-10 h-10 rounded-full border border-cream/20 grid place-items-center hover:bg-cream/10 transition" aria-label="Facebook"><Facebook size={16} /></a>
                <a href="#" className="w-10 h-10 rounded-full border border-cream/20 grid place-items-center hover:bg-cream/10 transition" aria-label="WhatsApp"><Send size={16} /></a>
              </div>
            </div>

            <div className="hidden lg:block lg:col-span-2">
              <div className="text-xs uppercase tracking-[0.2em] text-amber-warm font-semibold mb-4">Jelajah</div>
              <ul className="space-y-2.5 text-sm">
                {navItems.map((n) => (
                  <li key={n.to}><Link to={n.to} className="text-cream/70 hover:text-amber-warm transition">{n.label}</Link></li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-2">
              <div className="text-xs uppercase tracking-[0.2em] text-amber-warm font-semibold mb-4">Akses</div>
              <ul className="space-y-2.5 text-sm">
                <li><Link to="/anggota" className="text-cream/70 hover:text-amber-warm">Ruang Anggota</Link></li>
                <li><Link to="/anggota/jualan" className="text-cream/70 hover:text-amber-warm">Jual di Koperasi</Link></li>
                <li><Link to="/lacak" className="text-cream/70 hover:text-amber-warm">Lacak Pesanan Tamu</Link></li>
                <li><Link to="/katalog" className="text-cream/70 hover:text-amber-warm">Katalog Produk</Link></li>
                <li><Link to="/admin" className="text-cream/70 hover:text-amber-warm">Dasbor Pengurus</Link></li>
                <li><Link to="/masuk" className="text-cream/70 hover:text-amber-warm">Masuk</Link></li>
                <li><Link to="/daftar" className="text-cream/70 hover:text-amber-warm">Daftar Anggota</Link></li>
              </ul>
            </div>

            <div className="lg:col-span-3">
              <div className="text-xs uppercase tracking-[0.2em] text-amber-warm font-semibold mb-4">Kontak</div>
              <ul className="space-y-3 text-sm text-cream/75">
                <li className="flex gap-3"><MapPin size={16} className="mt-0.5 shrink-0 text-amber-warm" /> {koperasiInfo.address}</li>
                <li className="flex gap-3"><Phone size={16} className="mt-0.5 shrink-0 text-amber-warm" /> {koperasiInfo.phone}</li>
                <li className="flex gap-3"><Mail size={16} className="mt-0.5 shrink-0 text-amber-warm" /> {koperasiInfo.email}</li>
                <li><Link to="/kontak" className="text-amber-warm hover:underline">Halaman Kontak →</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-cream/10 mt-10 lg:mt-12 pt-6 flex flex-col md:flex-row justify-between gap-3 text-xs text-cream/55">
            <div>© {new Date().getFullYear()} {koperasiInfo.name}. Badan Hukum No. {koperasiInfo.legalNumber}.</div>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              <Link to="/syarat-ketentuan" className="hover:text-amber-warm">Syarat & Ketentuan</Link>
              <Link to="/kebijakan-pengembalian" className="hover:text-amber-warm">Kebijakan Pengembalian Dana</Link>
              <Link to="/kebijakan-privasi" className="hover:text-amber-warm">Kebijakan Privasi</Link>
              <Link to="/kontak" className="hover:text-amber-warm">Kontak</Link>
            </div>
          </div>
        </div>
      </footer>

      <MobileTabBar items={tabs} testIdPrefix="portal-tab" />
    </div>
  );
}
